import { ErrorHandler, inject, Injectable, Injector } from '@angular/core';

import { LoggerService } from './logger.service';
import { ToastService } from './toast.service';

/**
 * Error patterns that are noisy but harmless — skip toast and log upload.
 */
const IGNORED_ERROR_PATTERNS: RegExp[] = [
  /ResizeObserver loop/i,
  /ExpressionChangedAfterItHasBeenCheckedError/,
  // Chunk loading failures are handled by the SW update service
  /Loading chunk .* failed/i,
  /Failed to fetch dynamically imported module/i,
];

/**
 * Global Error Handler
 *
 * Overrides Angular's default ErrorHandler to:
 * 1. Show an error toast so the user knows something went wrong.
 * 2. Automatically send FE logs to the backend for diagnostics.
 *
 * Angular's `provideBrowserGlobalErrorListeners()` (registered in app.config.ts)
 * routes browser-level `window.onerror` and `unhandledrejection` events through
 * this handler, so it catches unhandled promise rejections as well.
 *
 * Safeguards:
 * - Deduplication: same error within 30 s only triggers once.
 * - Rate-limited log upload: at most one upload per 60 s.
 * - Services obtained lazily via Injector to avoid circular-dependency issues
 *   that occur when ErrorHandler is resolved before most services.
 */
@Injectable()
export class GlobalErrorHandlerService implements ErrorHandler {
  private readonly injector = inject(Injector);

  /** Minimum gap between log uploads (ms). */
  private readonly LOG_UPLOAD_COOLDOWN_MS = 60_000;
  /** Window within which identical errors are suppressed (ms). */
  private readonly ERROR_DEDUP_WINDOW_MS = 30_000;

  private lastLogSentAt = 0;
  /** fingerprint → timestamp of last occurrence */
  private readonly recentErrors = new Map<string, number>();

  handleError(error: unknown): void {
    // Always surface to the console so devtools still shows the error.
    window.console.error('[GlobalErrorHandler] Unhandled error:', error);

    // Also capture through LoggerService so the error appears in the ring buffer
    // and is included in any subsequent sendLogsToBackend() calls. Without this,
    // the actual error is invisible in backend logs (only shows in browser DevTools).
    try {
      const loggerService = this.injector.get(LoggerService);
      const logger = loggerService.getLogger('global-error');
      const message = this.extractMessage(error);
      const stack = error instanceof Error ? error.stack : undefined;
      logger.error('Unhandled error:', message, stack ?? error);
    } catch {
      // LoggerService not yet available — ignore
    }

    const message = this.extractMessage(error);

    if (IGNORED_ERROR_PATTERNS.some((re) => re.test(message))) {
      return;
    }

    // Local dedup window (both reads are on this device); this handler must also work
    // before Angular DI is ready (see the no-console exemption below), so it can't
    // reliably inject ClockSyncService anyway.
    // eslint-disable-next-line no-restricted-syntax
    const now = Date.now();
    const fingerprint = message.substring(0, 120);

    // Deduplication check
    const lastSeenAt = this.recentErrors.get(fingerprint) ?? 0;
    const isDuplicate = now - lastSeenAt < this.ERROR_DEDUP_WINDOW_MS;
    this.recentErrors.set(fingerprint, now);

    // Prune stale entries to avoid unbounded growth
    for (const [key, ts] of this.recentErrors) {
      if (now - ts > this.ERROR_DEDUP_WINDOW_MS) {
        this.recentErrors.delete(key);
      }
    }

    if (isDuplicate) {
      return;
    }

    // Show error toast
    try {
      const toast = this.injector.get(ToastService);
      toast.error('An unexpected error occurred. Diagnostic logs have been sent.', true, 8000);
    } catch {
      // ToastService not yet available (very early bootstrap error) — ignore
    }

    // Send logs to backend (rate-limited)
    if (now - this.lastLogSentAt > this.LOG_UPLOAD_COOLDOWN_MS) {
      this.lastLogSentAt = now;
      try {
        const loggerService = this.injector.get(LoggerService);
        loggerService.sendLogsToBackend().catch((err: unknown) => {
          window.console.error('[GlobalErrorHandler] Failed to send logs:', err);
        });
      } catch {
        // LoggerService not yet available — ignore
      }
    }
  }

  private extractMessage(error: unknown): string {
    if (error instanceof Error) return error.message;
    if (typeof error === 'string') return error;
    try {
      return JSON.stringify(error);
    } catch {
      return String(error);
    }
  }
}
